import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Spin,
  message,
  Typography,
  Modal,
  Radio,
  Descriptions,
  Alert,
  Table,
  Divider
} from 'antd';
import {
  CrownOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  StarOutlined,
  RocketOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import governanceService from '@services/operator/governanceService';
import ListPageHeader from '@shared/ListPageHeader';

const { Title, Text, Paragraph } = Typography;

const PLAN_ICONS = {
  standard: StarOutlined,
  pro: RocketOutlined,
  enterprise: CrownOutlined
};

const PLAN_COLORS = {
  standard: '#faad14',  // warning/gold - matches public site
  pro: '#1890ff',       // primary blue - matches public site
  enterprise: '#262626' // dark - matches public site
};

const PLAN_BG_COLORS = {
  standard: '#fffbe6',
  pro: '#e6f7ff',
  enterprise: '#fafafa'
};

const PricingPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [billingCycleModalVisible, setBillingCycleModalVisible] = useState(false);
  const [processing, setProcessing] = useState(false);
  const isEnterprise = currentSubscription?.pricing_plan?.plan_type === 'enterprise';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch pricing plans first (required)
      const plansResponse = await governanceService.getPricingPlans();
      if (plansResponse.success) {
        setPlans(plansResponse.data.pricing_plans);
      }

      // Fetch subscription separately (optional - may not exist)
      try {
        const subResponse = await governanceService.getSubscription();
        if (subResponse.success) {
          setCurrentSubscription(subResponse.data.subscription);
          setBillingCycle(subResponse.data.subscription?.billing_cycle || 'annual');
        }
      } catch (subError) {
        // Subscription not found is OK - user may not have one yet
        console.log('No existing subscription found');
      }
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectPlan = (plan) => {
    if (isCurrentPlan(plan)) {
      return;
    }
    if (isEnterprise) {
      return;
    }
    if (isCustomPricing(plan)) {
      window.open('mailto:gowarehouse-sales@ibiza.com.tw?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }
    // Handle same plan different cycle (billing cycle change only) - show confirmation first
    if (isSamePlanDifferentCycle(plan)) {
      setBillingCycleModalVisible(true);
      return;
    }
    setSelectedPlan(plan);
    setUpgradeModalVisible(true);
  };

  // 試算 + 若有可作廢的未付 invoice，先彈確認 modal；使用者按確定才執行真正的 API
  // performAction 不接收任何 invoice id — modal 純 UX，作廢由後端自己做
  const runWithSupersedeConfirm = async (planId, cycle, performAction) => {
    try {
      const preview = await governanceService.previewPlanChange(planId, cycle);
      if (!preview || !preview.success) {
        message.error(preview?.message || t('common.error.update_failed'));
        return false;
      }

      const data = preview.data || {};
      const supersedable = data.superseded_invoices || [];
      if (!data.requires_confirmation || supersedable.length === 0) {
        await performAction();
        return true;
      }

      return await new Promise((resolve) => {
        Modal.confirm({
          title: t('operator.governance.pricing_plans.plan_change_confirm_title'),
          width: 520,
          content: (
            <div>
              <p>{t('operator.governance.pricing_plans.plan_change_confirm_supersede_intro')}</p>
              <ul>
                {supersedable.map((inv) => (
                  <li key={inv.id}>
                    {inv.invoice_number} — {inv.currency} {Number(inv.total).toLocaleString()}
                  </li>
                ))}
              </ul>
              {data.estimated_charge && (
                <p>
                  {t('operator.governance.pricing_plans.plan_change_confirm_new_charge', {
                    amount: `${data.estimated_charge.currency} ${Number(data.estimated_charge.total).toLocaleString()}`,
                  })}
                </p>
              )}
            </div>
          ),
          okText: t('operator.governance.pricing_plans.plan_change_confirm_proceed'),
          cancelText: t('operator.governance.pricing_plans.plan_change_confirm_cancel'),
          onOk: async () => {
            await performAction();
            resolve(true);
          },
          onCancel: () => resolve(false),
        });
      });
    } catch (error) {
      console.error('previewPlanChange failed', error);
      message.error(t('common.error.update_failed'));
      return false;
    }
  };

  const handleChangeBillingCycle = async () => {
    try {
      setProcessing(true);
      // 換週期前先 preview；若有可作廢的未付 invoice，先彈確認 modal
      const currentPlanId = currentSubscription?.pricing_plan?.id;
      let response = null;
      const proceeded = await runWithSupersedeConfirm(currentPlanId, billingCycle, async () => {
        response = await governanceService.changeBillingCycle(billingCycle);
      });
      if (!proceeded || !response) {
        return;
      }

      if (response.success) {
        // Check if there's a pending invoice that requires payment
        if (response.data?.requires_payment && response.data?.pending_invoice) {
          const invoice = response.data.pending_invoice;
          const paymentMethod = response.data.payment_method;
          const paymentStatus = response.data.payment_status;

          if (paymentMethod === 'bank_transfer') {
            // ATM/bank transfer - redirect to invoices page with transfer info
            message.info(t('operator.governance.pricing_plans.billing_cycle_change_atm_notice', {
              amount: invoice.total.toLocaleString(),
              currency: invoice.currency
            }));
            navigate('/governance/invoices');
          } else if (paymentStatus === 'paid') {
            // Auto-charge succeeded
            message.success(t('operator.governance.pricing_plans.billing_cycle_changed_and_paid'));
            navigate('/governance/subscription');
          } else if (paymentStatus === 'failed') {
            // Auto-charge failed, retry queued
            Modal.warning({
              title: t('operator.governance.pricing_plans.payment_failed_title'),
              content: t('operator.governance.pricing_plans.payment_failed_will_retry'),
              onOk: () => navigate('/governance/invoices')
            });
          } else if (paymentStatus === 'no_payment_method') {
            // No payment method bound
            message.info(t('operator.governance.pricing_plans.billing_cycle_change_add_card'));
            navigate('/governance/payment_methods');
          } else {
            message.success(t('operator.governance.pricing_plans.billing_cycle_change_success'));
            navigate('/governance/invoices');
          }
        } else {
          message.success(t('operator.governance.pricing_plans.billing_cycle_change_success'));
          navigate('/governance/subscription');
        }
      } else if (response.message === 'annual_to_monthly_not_allowed') {
        message.error(t('operator.governance.pricing_plans.annual_to_monthly_not_allowed'));
      } else if (response.message === 'pending_invoice_blocks_change') {
        message.error(t('operator.governance.pricing_plans.pending_invoice_blocks_change'));
      } else {
        message.error(response.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to change billing cycle:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      setProcessing(true);
      let response;

      // 關閉首層 modal — 若有 supersede confirm 會在 body 彈第二層
      setUpgradeModalVisible(false);

      // 如果沒有現有訂閱，使用 subscribe 建立首次訂閱 (沒有舊 invoice 可作廢，不需 preview)
      if (!currentSubscription) {
        response = await governanceService.subscribeNewPlan(selectedPlan.id, billingCycle);
      } else {
        const isUpgrade = getPlanTypeOrder(selectedPlan.plan_type) >
          getPlanTypeOrder(currentSubscription?.pricing_plan?.plan_type);

        const apiCall = isUpgrade
          ? () => governanceService.upgradeSubscription(selectedPlan.id, billingCycle)
          : () => governanceService.downgradeSubscription(selectedPlan.id);

        const proceeded = await runWithSupersedeConfirm(selectedPlan.id, billingCycle, async () => {
          response = await apiCall();
        });
        if (!proceeded || !response) {
          // 使用者取消 / preview 失敗 — 訊息已由 runWithSupersedeConfirm 處理
          return;
        }
      }

      if (response.success) {
        setUpgradeModalVisible(false);

        // Handle auto-payment response (upgrade may generate pending invoice)
        if (response.data?.requires_payment && response.data?.pending_invoice) {
          const invoice = response.data.pending_invoice;
          const paymentMethod = response.data.payment_method;
          const paymentStatus = response.data.payment_status;

          if (paymentMethod === 'bank_transfer') {
            message.info(t('operator.governance.pricing_plans.upgrade_atm_notice', {
              amount: invoice.total.toLocaleString(),
              currency: invoice.currency
            }));
            navigate('/governance/invoices');
          } else if (paymentStatus === 'paid') {
            message.success(t('operator.governance.pricing_plans.upgrade_and_paid'));
            navigate('/governance/subscription');
          } else if (paymentStatus === 'failed') {
            Modal.warning({
              title: t('operator.governance.pricing_plans.payment_failed_title'),
              content: t('operator.governance.pricing_plans.payment_failed_will_retry'),
              onOk: () => navigate('/governance/invoices')
            });
          } else if (paymentStatus === 'no_payment_method') {
            message.info(t('operator.governance.pricing_plans.upgrade_add_card'));
            navigate('/governance/payment_methods');
          } else {
            message.success(t('operator.governance.pricing_plans.change_success'));
            navigate('/governance/invoices');
          }
        } else {
          const successMsg = currentSubscription
            ? t('operator.governance.pricing_plans.change_success')
            : t('operator.governance.pricing_plans.subscribe_success');
          message.success(successMsg);
          navigate('/governance/subscription');
        }
      } else if (response.message === 'pending_invoice_blocks_change') {
        message.error(t('operator.governance.pricing_plans.pending_invoice_blocks_change'));
      } else if (response.message === 'cannot_downgrade_multi_merchant') {
        message.error(t('operator.governance.pricing_plans.cannot_downgrade_multi_merchant'));
      } else {
        message.error(response.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to change plan:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setProcessing(false);
    }
  };

  const getPlanTypeOrder = (planType) => {
    const order = { standard: 0, pro: 1, enterprise: 2 };
    return order[planType] ?? -1;
  };

  const isCustomPricing = (plan) => {
    return plan.features?.custom_pricing === true ||
      (plan.monthly_price === 0 && plan.annual_price === 0 && plan.plan_type === 'enterprise');
  };

  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.annual_price : plan.monthly_price;
  };

  const getCurrencySymbol = (plan) => {
    return plan.currency === 'USD' ? 'US$' : 'NT$';
  };

  const getCustomPricingText = (plan) => {
    if (plan.features?.custom_pricing_note) {
      return plan.features.custom_pricing_note;
    }
    return plan.currency === 'USD'
      ? t('operator.governance.pricing_plans.contact_sales')
      : t('operator.governance.pricing_plans.custom_quote');
  };

  const calculateSavings = (plan) => {
    if (isCustomPricing(plan)) return { savings: 0, percentage: 0 };
    const monthlyCost = plan.monthly_price * 12;
    const yearlyCost = plan.annual_price;
    const savings = monthlyCost - yearlyCost;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { savings, percentage };
  };

  // Check if this is exactly the current plan (same plan AND same billing cycle)
  const isCurrentPlan = (plan) => {
    const isSamePlan = currentSubscription?.pricing_plan?.id === plan.id;
    const isSameCycle = currentSubscription?.billing_cycle === billingCycle;
    return isSamePlan && isSameCycle;
  };

  // Check if this is the same plan but different billing cycle (can switch)
  const isSamePlanDifferentCycle = (plan) => {
    const isSamePlan = currentSubscription?.pricing_plan?.id === plan.id;
    const isDifferentCycle = currentSubscription?.billing_cycle !== billingCycle;
    return isSamePlan && isDifferentCycle;
  };

  const getPlanTagline = (planType) => {
    const taglines = {
      standard: t('operator.governance.plan.taglines.standard'),
      pro: t('operator.governance.plan.taglines.pro'),
      enterprise: t('operator.governance.plan.taglines.enterprise')
    };
    return taglines[planType] || '';
  };

  const getPlanDescription = (planType) => {
    const descriptions = {
      standard: t('operator.governance.plan.descriptions.standard'),
      pro: t('operator.governance.plan.descriptions.pro'),
      enterprise: t('operator.governance.plan.descriptions.enterprise')
    };
    return descriptions[planType] || '';
  };

  const renderFeatureList = (plan) => {
    const features = [];
    const f = (key, opts) => t(`operator.governance.plan.features.${key}`, opts);

    if (plan.plan_type === 'standard') {
      // Standard plan card features
      features.push({ text: f('single_warehouse'), included: true });
      features.push({ text: f('no_tenant'), included: true });
      features.push({ text: f('account_limit', { count: plan.features.max_wms_users || 5 }), included: true });
      features.push({ text: f('ec_connect_limit', { count: 5 }), included: true });
      features.push({ text: f('web_only'), included: true });
      features.push({ text: f('video_30_days'), included: true });
      features.push({ text: f('shipping_volume', { count: 10 }), included: true });
      features.push({ text: f('ai_credits', { count: 600 }), included: true });
      features.push({ text: f('support_ticket'), included: true });
      features.push({ text: f('online_training'), included: true });
    } else if (plan.plan_type === 'pro') {
      // Pro plan card features
      features.push({ text: f('warehouse_limit', { count: plan.features.base_warehouses || 3 }), included: true });
      features.push({ text: f('tenant_limit', { count: 30 }), included: true });
      features.push({ text: f('account_limit', { count: plan.features.max_wms_users || 20 }), included: true });
      features.push({ text: f('ec_connect_unlimited'), included: true });
      features.push({ text: f('app_support'), included: true });
      features.push({ text: f('video_180_days'), included: true });
      features.push({ text: f('shipping_volume', { count: 30 }), included: true });
      features.push({ text: f('ai_credits', { count: '2,500' }), included: true });
      features.push({ text: f('one_click_billing'), included: true });
      features.push({ text: f('priority_support'), included: true });
      features.push({ text: f('online_training'), included: true });
    } else {
      // Enterprise plan card features
      features.push({ text: f('unlimited_warehouses'), included: true });
      features.push({ text: f('tenant_unlimited'), included: true });
      features.push({ text: f('unlimited_accounts'), included: true });
      features.push({ text: f('ec_connect_unlimited'), included: true });
      features.push({ text: f('app_support'), included: true });
      features.push({ text: f('video_custom'), included: true });
      features.push({ text: f('shipping_volume_custom'), included: true });
      features.push({ text: f('ai_credits_custom'), included: true });
      features.push({ text: f('dedicated_consultant'), included: true });
      features.push({ text: f('api_integration'), included: true });
      features.push({ text: f('onsite_training'), included: true });
      features.push({ text: f('dedicated_database'), included: true });
    }

    return features;
  };

  // Comparison table columns and data
  const comparisonColumns = [
    {
      title: t('operator.governance.pricing_plans.comparison.feature'),
      dataIndex: 'feature',
      key: 'feature',
      width: 200,
      fixed: 'left',
      render: (text, record) => record.isGroupHeader
        ? <Text strong style={{ fontSize: 13, color: '#1890ff' }}>{text}</Text>
        : <Text>{text}</Text>
    },
    {
      title: t('operator.governance.plan.types.standard'),
      dataIndex: 'standard',
      key: 'standard',
      align: 'center',
      width: 150,
      render: (value, record) => renderComparisonValue(value, record)
    },
    {
      title: t('operator.governance.plan.types.pro'),
      dataIndex: 'pro',
      key: 'pro',
      align: 'center',
      width: 150,
      className: 'bg-blue-50',
      render: (value, record) => renderComparisonValue(value, record)
    },
    {
      title: t('operator.governance.plan.types.enterprise'),
      dataIndex: 'enterprise',
      key: 'enterprise',
      align: 'center',
      width: 150,
      render: (value, record) => renderComparisonValue(value, record)
    }
  ];

  const renderComparisonValue = (value, record) => {
    if (record?.isGroupHeader) return null;
    if (value === true) {
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />;
    }
    if (value === false) {
      return <CloseCircleFilled style={{ color: '#d9d9d9', fontSize: 16 }} />;
    }
    return <Text>{value}</Text>;
  };

  // Feature groups for the dynamic comparison table
  // Features are displayed in this order, grouped by category
  const FEATURE_GROUPS = [
    {
      group: 'order_management',
      features: ['orders', 'order_templates', 'orders.out_of_stock', 'orders.merge',
                 'return_orders', 'orders.serial_numbers', 'orders.sla']
    },
    {
      group: 'warehouse_operations',
      features: ['inbounds', 'shelves', 'picking_lists', 'picking_list_presets',
                 'picking_lists.app_picking', 'fulfillments', 'fulfillments.qc',
                 'fulfillments.qc_recording', 'stock_takings',
                 'shipments', 'shipments.batches', 'shipments.upload_labels']
    },
    {
      group: 'inventory',
      features: ['inventories', 'inventory_snapshots', 'inventory_monitors',
                 'stock_adjustments']
    },
    {
      group: 'product_management',
      features: ['products', 'bundles', 'brands', 'flexible_products',
                 'products.replace_sku', 'production_boms', 'consumables']
    },
    {
      group: 'advanced',
      features: ['mobile_app', 'ai_assistant', 'sales_channels', 'billings',
                 'value_added_services', 'warehouse_broadcasts',
                 'accessible_ip_addresses', 'shipping_types.custom', 'merchant_shipping_types']
    },
    {
      group: 'enterprise_only',
      features: ['replenishment_orders', 'dedicated_infrastructure', 'erp_integration', 'custom_development']
    }
  ];

  // Build comparison data dynamically from API feature_comparison
  const buildComparisonData = () => {
    const plansByType = {};
    plans.forEach(plan => { plansByType[plan.plan_type] = plan; });

    const rows = [];

    // Specs rows (方案規格 — text values, not boolean)
    const c = (key) => t(`operator.governance.pricing_plans.comparison.${key}`);
    const v = (key) => t(`operator.governance.pricing_plans.comparison.values.${key}`);

    rows.push(
      { key: 'architecture', feature: c('architecture'), standard: v('architecture_standard'), pro: v('architecture_pro'), enterprise: v('architecture_enterprise') },
      { key: 'warehouse_tenant', feature: c('warehouse_tenant'), standard: v('warehouse_tenant_standard'), pro: v('warehouse_tenant_pro'), enterprise: v('warehouse_tenant_enterprise') },
      { key: 'accounts', feature: c('accounts'), standard: v('accounts_standard'), pro: v('accounts_pro'), enterprise: v('accounts_enterprise') },
      { key: 'ec_connect', feature: c('ec_connect'), standard: v('ec_connect_standard'), pro: v('ec_connect_pro'), enterprise: v('ec_connect_enterprise') },
      { key: 'shipping_pcs', feature: c('shipping_pcs'), standard: v('shipping_standard'), pro: v('shipping_pro'), enterprise: v('shipping_enterprise') },
      { key: 'overage_fee', feature: c('overage_fee'), standard: v('overage_fee_standard'), pro: v('overage_fee_pro'), enterprise: v('overage_fee_enterprise') },
      { key: 'ai_credits', feature: c('ai_credits'), standard: v('ai_credits_standard'), pro: v('ai_credits_pro'), enterprise: v('ai_credits_enterprise') },
      { key: 'video', feature: c('video'), standard: v('video_standard'), pro: v('video_pro'), enterprise: v('video_enterprise') },
      { key: 'scalability', feature: c('scalability'), standard: v('scalability_standard'), pro: v('scalability_pro'), enterprise: v('scalability_enterprise') },
      { key: 'support', feature: c('support'), standard: v('support_standard'), pro: v('support_pro'), enterprise: v('support_enterprise') },
      { key: 'training', feature: c('training'), standard: v('training_standard'), pro: v('training_pro'), enterprise: v('training_enterprise') }
    );

    // Dynamic feature rows from API feature_comparison
    FEATURE_GROUPS.forEach(({ group, features }) => {
      // Add group header row
      rows.push({
        key: `group_${group}`,
        feature: t(`operator.governance.pricing_plans.comparison.groups.${group}`),
        isGroupHeader: true
      });

      features.forEach(featureKey => {
        const featureLabel = t(`operator.governance.pricing_plans.comparison.feature_keys.${featureKey}`, featureKey);
        const row = {
          key: featureKey,
          feature: featureLabel,
          standard: plansByType.standard?.feature_comparison?.[featureKey] ?? false,
          pro: plansByType.pro?.feature_comparison?.[featureKey] ?? false,
          enterprise: plansByType.enterprise?.feature_comparison?.[featureKey] ?? true
        };
        rows.push(row);
      });
    });

    return rows;
  };

  const comparisonData = buildComparisonData();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <ListPageHeader items={['governance', 'governance.pricing_plans']} hideTitle />

      {isEnterprise && (
        <Alert
          message={t('operator.governance.pricing_plans.enterprise_notice')}
          type="info"
          showIcon
          icon={<CrownOutlined />}
          style={{ marginBottom: 24, textAlign: 'center' }}
        />
      )}

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('operator.governance.pricing_plans.title')}
        </Title>
        <Paragraph type="secondary" style={{ fontSize: 16, maxWidth: 600, margin: '0 auto 24px' }}>
          {t('operator.governance.pricing_plans.description')}
        </Paragraph>

        {/* Billing Cycle Toggle */}
        {!isEnterprise && (
          <div style={{
            display: 'inline-flex',
            background: '#f5f5f5',
            padding: 4,
            borderRadius: 8,
            border: '1px solid #e8e8e8'
          }}>
            <Button
              type={billingCycle === 'monthly' ? 'primary' : 'text'}
              onClick={() => setBillingCycle('monthly')}
              style={{
                borderRadius: 6,
                fontWeight: 600,
                ...(billingCycle !== 'monthly' && { background: 'transparent' })
              }}
            >
              {t('operator.governance.subscription.cycles.monthly')}
            </Button>
            <Button
              type={billingCycle === 'annual' ? 'primary' : 'text'}
              onClick={() => setBillingCycle('annual')}
              style={{
                borderRadius: 6,
                fontWeight: 600,
                ...(billingCycle !== 'annual' && { background: 'transparent' })
              }}
            >
              {t('operator.governance.subscription.cycles.annual')}
            </Button>
          </div>
        )}
      </div>

      {/* Pricing Cards */}
      <Row gutter={[16, 16]} justify="center" style={{ marginBottom: 32 }}>
        {plans.map((plan) => {
          const PlanIcon = PLAN_ICONS[plan.plan_type] || StarOutlined;
          const planColor = PLAN_COLORS[plan.plan_type] || '#1890ff';
          const isCurrent = isCurrentPlan(plan);
          const canSwitchCycle = isSamePlanDifferentCycle(plan);
          const isPopular = plan.plan_type === 'pro';
          const { percentage } = calculateSavings(plan);

          return (
            <Col key={plan.id} xs={24} sm={24} md={8}>
              <Card
                hoverable={!isCurrent}
                style={{
                  borderColor: isPopular ? planColor : isCurrent ? '#52c41a' : '#e8e8e8',
                  borderWidth: isPopular || isCurrent ? 2 : 1,
                  height: '100%',
                  position: 'relative',
                  overflow: 'visible'
                }}
                styles={{
                  body: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 20
                  }
                }}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <Tag
                    color="blue"
                    icon={<ThunderboltOutlined />}
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: -4,
                      borderRadius: '4px 0 0 4px',
                      fontWeight: 600,
                      fontSize: 10
                    }}
                  >
                    {t('operator.governance.pricing_plans.popular')}
                  </Tag>
                )}

                {/* Plan Name */}
                <Title level={4} style={{ marginBottom: 4 }}>
                  {t(`operator.governance.plan.types.${plan.plan_type}`)}
                </Title>

                {/* Tagline */}
                <Text style={{ color: planColor, fontWeight: 500, fontSize: 12, marginBottom: 8 }}>
                  {getPlanTagline(plan.plan_type)}
                </Text>

                {/* Description */}
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16, minHeight: 40 }}>
                  {getPlanDescription(plan.plan_type)}
                </Paragraph>

                {/* Price */}
                <div style={{
                  borderBottom: '1px solid #f0f0f0',
                  paddingBottom: 16,
                  marginBottom: 16,
                  minHeight: 80
                }}>
                  {isCustomPricing(plan) ? (
                    <div>
                      <Text strong style={{ fontSize: 18, color: '#262626', whiteSpace: 'pre-line' }}>
                        {getCustomPricingText(plan)}
                      </Text>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <Text strong style={{ fontSize: 28 }}>
                          {getCurrencySymbol(plan)} {getPrice(plan).toLocaleString()}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 14 }}>
                          / {billingCycle === 'annual' ? t('common.year') : t('common.month')}
                        </Text>
                      </div>
                      {billingCycle === 'annual' && percentage > 0 && (
                        <Tag color="green" style={{ marginTop: 8, fontSize: 10 }}>
                          {t('operator.governance.pricing_plans.save_percent', { percent: percentage })}
                        </Tag>
                      )}
                      {/* Tax notice for Taiwan (TWD) */}
                      {plan.currency === 'TWD' && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                          {t('operator.governance.pricing_plans.tax_notice_tw')}
                        </Text>
                      )}
                    </>
                  )}
                </div>

                {/* Features */}
                <div style={{ flex: 1, marginBottom: 16 }}>
                  {renderFeatureList(plan)
                    .filter(f => f.included)
                    .slice(0, 8)
                    .map((feature, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <CheckCircleFilled style={{ color: planColor, marginTop: 3, fontSize: 14 }} />
                        <Text style={{ fontSize: 12, lineHeight: '20px' }}>{feature.text}</Text>
                      </div>
                    ))}
                </div>

                {/* Trial Info - only show for new users without subscription */}
                {plan.trial_days > 0 && !currentSubscription && (
                  <Alert
                    message={t('operator.governance.pricing_plans.trial_days', { days: plan.trial_days })}
                    type="info"
                    showIcon
                    style={{ marginBottom: 12, fontSize: 12 }}
                  />
                )}

                {/* CTA Button */}
                <Button
                  type="primary"
                  size="large"
                  block
                  disabled={isCurrent || isEnterprise}
                  loading={canSwitchCycle && processing}
                  onClick={() => handleSelectPlan(plan)}
                  style={{
                    backgroundColor: isCurrent ? '#52c41a' : canSwitchCycle ? '#fa8c16' : planColor,
                    borderColor: isCurrent ? '#52c41a' : canSwitchCycle ? '#fa8c16' : planColor,
                    fontWeight: 600
                  }}
                >
                  {isCurrent
                    ? t('operator.governance.pricing_plans.current_plan')
                    : isEnterprise
                      ? t('operator.governance.pricing_plans.enterprise_contact_manager')
                      : canSwitchCycle
                        ? t(`operator.governance.pricing_plans.switch_to_${billingCycle}`)
                        : isCustomPricing(plan)
                          ? t('operator.governance.pricing_plans.contact_us')
                          : currentSubscription
                            ? t('operator.governance.pricing_plans.select')
                            : t('operator.governance.pricing_plans.subscribe_cta')}
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Comparison Table */}
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            {t('operator.governance.pricing_plans.comparison.title')}
          </Title>
        }
        style={{ marginTop: 32 }}
      >
        <Table
          columns={comparisonColumns}
          dataSource={comparisonData}
          pagination={false}
          size="small"
          scroll={{ x: 650 }}
          rowClassName={(record, index) => record.isGroupHeader ? 'bg-blue-50' : (index % 2 === 0 ? '' : 'bg-gray-50')}
        />
      </Card>

      {/* Upgrade/Subscribe Confirmation Modal */}
      <Modal
        title={currentSubscription
          ? t('operator.governance.pricing_plans.confirm_change')
          : t('operator.governance.pricing_plans.confirm_subscribe')}
        open={upgradeModalVisible}
        onOk={handleUpgrade}
        onCancel={() => setUpgradeModalVisible(false)}
        confirmLoading={processing}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        width={500}
      >
        {selectedPlan && (() => {
          const isCycleChanging = currentSubscription && billingCycle !== currentSubscription.billing_cycle;
          const isTrialing = currentSubscription?.status === 'trialing';
          const isUpgrading = currentSubscription && getPlanTypeOrder(selectedPlan.plan_type) >
            getPlanTypeOrder(currentSubscription?.pricing_plan?.plan_type);
          const isDowngrading = currentSubscription && getPlanTypeOrder(selectedPlan.plan_type) <
            getPlanTypeOrder(currentSubscription?.pricing_plan?.plan_type);

          // Estimated charge calculation (approximate, assumes day-1 of cycle)
          let estimatedCharge = null;
          if (currentSubscription && isUpgrading && !isTrialing) {
            if (isCycleChanging && billingCycle === 'annual') {
              // Monthly → annual + plan upgrade: new annual - old monthly credit
              estimatedCharge = selectedPlan.annual_price - currentSubscription.pricing_plan.monthly_price;
            } else {
              // Same cycle: price difference
              const currentPrice = currentSubscription.billing_cycle === 'annual'
                ? currentSubscription.pricing_plan.annual_price
                : currentSubscription.pricing_plan.monthly_price;
              const newPrice = billingCycle === 'annual' ? selectedPlan.annual_price : selectedPlan.monthly_price;
              estimatedCharge = newPrice - currentPrice;
            }
          }

          return (
            <>
              {/* 升級 + 換週期時顯示提醒 */}
              {isCycleChanging && isUpgrading && !isTrialing && billingCycle === 'annual' && (
                <Alert
                  style={{ marginBottom: 16 }}
                  message={t('operator.governance.pricing_plans.billing_cycle_change_warning')}
                  type="warning"
                  showIcon
                />
              )}

              <Descriptions bordered column={1} size="small">
                {/* 只有當用戶已有訂閱時才顯示「原方案」 */}
                {currentSubscription && (
                  <Descriptions.Item label={t('operator.governance.pricing_plans.from')}>
                    {t(`operator.governance.plan.types.${currentSubscription.pricing_plan?.plan_type}`)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={currentSubscription
                  ? t('operator.governance.pricing_plans.to')
                  : t('operator.governance.pricing_plans.selected_plan')}>
                  <Text strong>{t(`operator.governance.plan.types.${selectedPlan.plan_type}`)}</Text>
                </Descriptions.Item>
                {/* 計費週期 - 顯示是否有變更 */}
                <Descriptions.Item label={t('operator.governance.subscription.billing_cycle')}>
                  {isCycleChanging ? (
                    <>
                      {t(`operator.governance.subscription.cycles.${currentSubscription.billing_cycle}`)}
                      {' → '}
                      <Text strong type="warning">{t(`operator.governance.subscription.cycles.${billingCycle}`)}</Text>
                    </>
                  ) : (
                    t(`operator.governance.subscription.cycles.${billingCycle}`)
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={currentSubscription
                  ? t('operator.governance.pricing_plans.new_price')
                  : t('operator.governance.pricing_plans.price')}>
                  <Text strong>
                    {getCurrencySymbol(selectedPlan)} {getPrice(selectedPlan).toLocaleString()} / {t(`operator.governance.subscription.cycles.${billingCycle}`)}
                  </Text>
                  {selectedPlan.currency === 'TWD' && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('operator.governance.pricing_plans.tax_notice_tw')}
                      </Text>
                    </div>
                  )}
                </Descriptions.Item>
                {/* 預估費用 - 升級時顯示 */}
                {estimatedCharge !== null && estimatedCharge > 0 && (
                  <Descriptions.Item label={t('operator.governance.pricing_plans.estimated_charge')}>
                    <Text strong>
                      {getCurrencySymbol(selectedPlan)}{' '}
                      {estimatedCharge.toLocaleString()}
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                        ({isCycleChanging
                          ? t('operator.governance.pricing_plans.annual_minus_monthly_credit')
                          : t('operator.governance.pricing_plans.annual_minus_monthly_credit')})
                      </Text>
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {t('operator.governance.pricing_plans.prorate_note')}
                      </Text>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>

              {/* 月轉年提醒 */}
              {isCycleChanging && isUpgrading && !isTrialing && billingCycle === 'annual' && (
                <Alert
                  style={{ marginTop: 16 }}
                  message={t('operator.governance.pricing_plans.monthly_to_annual_notice')}
                  type="info"
                  showIcon
                />
              )}

              {/* 降級警告 */}
              {isDowngrading && (
                <Alert
                  style={{ marginTop: 16 }}
                  message={t('operator.governance.pricing_plans.downgrade_notice')}
                  description={t('operator.governance.pricing_plans.downgrade_desc')}
                  type="warning"
                  showIcon
                />
              )}

              {/* 新訂閱時顯示試用期資訊 */}
              {!currentSubscription && selectedPlan.trial_days > 0 && (
                <Alert
                  style={{ marginTop: 16 }}
                  message={t('operator.governance.pricing_plans.trial_days', { days: selectedPlan.trial_days })}
                  type="info"
                  showIcon
                />
              )}
            </>
          );
        })()}
      </Modal>

      {/* Billing Cycle Change Confirmation Modal */}
      {(() => {
        const isTrialing = currentSubscription?.status === 'trialing';
        const isAnnualToMonthly = billingCycle === 'monthly' && currentSubscription?.billing_cycle === 'annual';
        // 未付年約可自助轉月繳（後端 gate：active + 當期年費全未付）；已付 / 停權則由後端擋回
        // 友善錯誤。因此不再硬性 disable，只顯示提示，由後端決定成敗。
        const isAnnualToMonthlySwitch = isAnnualToMonthly && !isTrialing;

        return (
          <Modal
            title={t('operator.governance.pricing_plans.confirm_billing_cycle_change')}
            open={billingCycleModalVisible}
            onOk={() => {
              setBillingCycleModalVisible(false);
              handleChangeBillingCycle();
            }}
            onCancel={() => setBillingCycleModalVisible(false)}
            confirmLoading={processing}
            okText={t('common.button.confirm')}
            cancelText={t('common.button.cancel')}
            width={500}
          >
            {currentSubscription && (
              <>
                <Alert
                  style={{ marginBottom: 16 }}
                  message={isTrialing
                    ? t('operator.governance.pricing_plans.billing_cycle_change_warning_trial')
                    : t('operator.governance.pricing_plans.billing_cycle_change_warning')
                  }
                  type={isTrialing ? 'info' : 'warning'}
                  showIcon
                />
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label={t('operator.governance.pricing_plans.current_plan')}>
                    <Text strong>{t(`operator.governance.plan.types.${currentSubscription.pricing_plan?.plan_type}`)}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operator.governance.pricing_plans.current_billing_cycle')}>
                    {t(`operator.governance.subscription.cycles.${currentSubscription.billing_cycle}`)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('operator.governance.pricing_plans.new_billing_cycle')}>
                    <Text strong type="warning">
                      {t(`operator.governance.subscription.cycles.${billingCycle}`)}
                    </Text>
                  </Descriptions.Item>
                  {!isTrialing && billingCycle === 'annual' && currentSubscription.pricing_plan && (
                    <Descriptions.Item label={t('operator.governance.pricing_plans.estimated_charge')}>
                      <Text strong>
                        {getCurrencySymbol(currentSubscription.pricing_plan)}{' '}
                        {(currentSubscription.pricing_plan.annual_price - currentSubscription.pricing_plan.monthly_price).toLocaleString()}
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          ({t('operator.governance.pricing_plans.annual_minus_monthly_credit')})
                        </Text>
                      </Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>

                {!isTrialing && billingCycle === 'annual' && (
                  <Alert
                    style={{ marginTop: 16 }}
                    message={t('operator.governance.pricing_plans.monthly_to_annual_notice')}
                    type="info"
                    showIcon
                  />
                )}

                {isAnnualToMonthlySwitch && (
                  <Alert
                    style={{ marginTop: 16 }}
                    message={t('operator.governance.pricing_plans.annual_to_monthly_warning')}
                    description={t('operator.governance.pricing_plans.annual_to_monthly_warning_desc')}
                    type="warning"
                    showIcon
                  />
                )}
              </>
            )}
          </Modal>
        );
      })()}
    </div>
  );
};

export default PricingPlans;
